import React, { useState, useEffect } from "react";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import StaticTemplatePreview from "@/components/StaticTemplatePreview";
import AspectToggle from "@/components/social/AspectToggle";
import { resolveManifestProperty, resolveManifestPhotoUrl } from "@/lib/manifestUrls";
import { usePatchedBrochureProperty } from "@/lib/brochurePhotos";
import { authedFileUrl } from "@/components/propertyDetail/helpers";

/**
 * PreviewBody — frame anteprima (statico / carosello / video) + controlli
 * specifici per formato (aspect toggle, before/after).
 *
 * Estratto da SocialStudioTab.jsx (iter 274) per ridurre il file principale.
 * Stateless: tutti gli state + handler vengono dal parent.
 */
export default function PreviewBody({
  active,
  isStatic,
  isCarousel,
  activeFormat,
  manifest,
  isPro,
  staticLayout,
  staticColor,
  staticPreviewRef,
  loading,
  previewUrl,
  previewError,
  onRetryPreview,
  carouselSlides,
  carouselIdx,
  setCarouselIdx,
  goPrev,
  goNext,
  reelAspect,
  setReelAspect,
  postAspect,
  setPostAspect,
  baAspect,
  setBaAspect,
  reelShowPrice,
  showPrice,
  setReelShowPrice,
  baLabelKind,
  setBaLabelKind,
  baPhotoId,
  setBaPhotoId,
  baPhotoIds = [],
  setBaPhotoIds,
  heroPhotoId,
  setHeroPhotoId,
  // iter r95 · Reel job progress passato dal parent: se presente
  // (elapsed+etaRemaining), lo mostro qui DENTRO il riquadro preview
  // invece di lasciare il banner duplicato esterno.
  reelJobProgress = null,
}) {
  // iter v26 r17 — user feedback: anche dopo che il fetch del MP4 finisce
  // (loading=false), il <video> impiega ~1-3s a decodificare il primo frame
  // e durante quel buffering si vede un quadrato nero, l'utente pensa che
  // sia rotto. Aggiungiamo uno stato locale `videoBuffering` che resta true
  // finché il video non emette `loadeddata` (= primo frame pronto da renderizzare).
  const [videoBuffering, setVideoBuffering] = useState(false);
  // Quando cambia il previewUrl di un video, ricomincia il buffering
  useEffect(() => {
    if ((active === "reel" || active === "before_after") && previewUrl) {
      setVideoBuffering(true);
    } else {
      setVideoBuffering(false);
    }
  }, [previewUrl, active]);
  // iter 275 round 42: per la brochure A4 dobbiamo applicare lo STESSO
  // preprocessing foto di BrochureExportModal (cap a 10 + verticali in
  // fondo) altrimenti la cover si rompe se la prima foto è verticale.
  const brochureProp = usePatchedBrochureProperty(
    manifest?.property,
    manifest?.property?.photoTotalCount,
  );
  return (
    <>
      {/* Preview frame */}
      {isStatic ? (
        <div className="mb-5" data-testid={`static-preview-${active}`} ref={staticPreviewRef}>
          <div
            className={`mx-auto ${
              active === "brochure"
                ? "w-full"
                : active === "brochure_social"
                ? "w-full max-w-[480px]"
                : active === "open_house" || active === "story_nuovo" || active === "story_venduto"
                ? "max-w-[260px]"
                : "max-w-[420px]"
            }`}
          >
            <StaticTemplatePreview
              template={active}
              layout={staticLayout}
              color={staticColor}
              property={resolveManifestProperty((active === "brochure" || active === "brochure_social") ? brochureProp : manifest.property)}
              agency={manifest.agency}
              showWatermark={!isPro}
              showPrice={showPrice}
            />
          </div>
        </div>
      ) : isCarousel ? (
        <div data-testid="carousel-preview">
          <div className="flex justify-center mb-4">
            <div className="aspect-square w-full max-w-[420px] rounded-lg overflow-hidden bg-[#F1F4ED] border border-[#E5E7E0] flex items-center justify-center relative">
              {loading || carouselSlides.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-3 p-6 text-center">
                  <div className="w-9 h-9 rounded-full border-3 border-[#7C8B6E] border-t-transparent animate-spin" />
                  <p className="text-[12px] font-semibold text-[#1C1C1A] leading-snug">
                    Sto preparando il carosello…
                  </p>
                  <p className="text-[10.5px] text-[#6B6A65] leading-snug">
                    5 slide ad alta risoluzione · 8-12 secondi
                  </p>
                </div>
              ) : (
                <>
                  <img
                    src={carouselSlides[carouselIdx]?.blobUrl}
                    alt={`Slide ${carouselIdx + 1}`}
                    className="w-full h-full object-cover transition-opacity duration-200"
                    data-testid={`carousel-slide-${carouselIdx + 1}`}
                  />
                  {/* Navigation arrows on top of preview */}
                  {carouselIdx > 0 && (
                    <button
                      onClick={goPrev}
                      className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/85 hover:bg-white border border-[#EAE7DF] shadow-sm flex items-center justify-center text-[#1C1C1A] transition-all"
                      aria-label="Slide precedente"
                      data-testid="carousel-prev"
                    >
                      <CaretLeft size={16} weight="bold" />
                    </button>
                  )}
                  {carouselIdx < carouselSlides.length - 1 && (
                    <button
                      onClick={goNext}
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/85 hover:bg-white border border-[#EAE7DF] shadow-sm flex items-center justify-center text-[#1C1C1A] transition-all"
                      aria-label="Slide successiva"
                      data-testid="carousel-next"
                    >
                      <CaretRight size={16} weight="bold" />
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Thumbnail strip */}
          {carouselSlides.length > 0 && (
            <div className="flex justify-center gap-2 mb-5 overflow-x-auto pb-1" data-testid="carousel-thumbs">
              {carouselSlides.map((s, i) => (
                <button
                  key={s.idx}
                  onClick={() => setCarouselIdx(i)}
                  className={`flex-shrink-0 w-14 h-14 rounded-md overflow-hidden border-2 transition-all ${
                    i === carouselIdx
                      ? "border-[#7C8B6E] ring-2 ring-[#7C8B6E]/30"
                      : "border-[#EAE7DF] opacity-70 hover:opacity-100"
                  }`}
                  aria-label={`Vai alla slide ${s.idx}`}
                  data-testid={`carousel-thumb-${s.idx}`}
                >
                  <img src={s.blobUrl} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="flex justify-center mb-5">
          <div
            className={`${activeFormat?.ratio} ${
              activeFormat?.ratio === "aspect-square" ? "w-full max-w-[420px]" : "w-full max-w-[260px]"
            } rounded-lg overflow-hidden bg-[#F1F4ED] border border-[#E5E7E0] flex items-center justify-center relative`}
          >
            {loading || !previewUrl ? (
              previewError && !loading ? (
                // iter v33 r54 · Card di errore actionable: quando la
                // generazione reel/post/story fallisce (es. HEIC pesante,
                // 503 server occupato, timeout) NON restiamo bloccati
                // sullo spinner. Mostriamo il messaggio + bottone Riprova.
                <div
                  className="flex flex-col items-center justify-center gap-3 p-6 text-center"
                  data-testid={`social-${active}-error`}
                >
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ background: "rgba(209,77,77,0.12)" }}
                  >
                    <span className="text-[#D14D4D] text-[20px] font-bold leading-none">!</span>
                  </div>
                  <p className="text-[12px] font-semibold text-[#1C1C1A] leading-snug max-w-[240px]">
                    {previewError}
                  </p>
                  {typeof onRetryPreview === "function" && (
                    <button
                      type="button"
                      onClick={onRetryPreview}
                      className="mt-1 px-3 py-1.5 rounded-full bg-[#7C8B6E] hover:bg-[#6E7B62] text-white text-[11px] font-semibold uppercase tracking-wider transition-colors"
                      data-testid={`social-${active}-error-retry`}
                    >
                      Riprova
                    </button>
                  )}
                </div>
              ) : (
              <div className="flex flex-col items-center justify-center gap-3 p-6 text-center" data-testid={`social-${active}-loading`}>
                <div className="w-10 h-10 rounded-full border-[3px] border-[#7C8B6E] border-t-transparent animate-spin" />
                {active === "reel" && (
                  <>
                    <p className="text-[12px] font-semibold text-[#1C1C1A] leading-snug">
                      Sto creando il reel…
                    </p>
                    {/* iter r95 · Progress inline (elapsed / eta / progress bar):
                        se il parent passa `reelJobProgress`, mostro qui i dettagli
                        così il loader è UNICO e sta dentro al riquadro preview. */}
                    {reelJobProgress ? (
                      <div className="w-full max-w-[220px] flex flex-col gap-2">
                        <p className="text-[10.5px] text-[#6B6A65] tabular-nums leading-snug">
                          {reelJobProgress.elapsed}s trascorsi
                          {reelJobProgress.etaRemaining > 0 && (
                            <span className="text-[#7C8B6E] font-semibold ml-1">
                              · ~{reelJobProgress.etaRemaining}s rimanenti
                            </span>
                          )}
                        </p>
                        <div className="h-1 bg-[#E5E7E0] rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#7C8B6E] transition-all duration-500 ease-out"
                            style={{
                              width: `${Math.min(95, Math.max(5, Math.round(
                                (reelJobProgress.elapsed / (reelJobProgress.elapsed + reelJobProgress.etaRemaining || 45)) * 100
                              )))}%`,
                            }}
                          />
                        </div>
                        <p className="text-[10px] text-[#9A968B] leading-snug">
                          Puoi chiudere l'app: al ritorno riprende in automatico.
                        </p>
                      </div>
                    ) : (
                      <p className="text-[10.5px] text-[#6B6A65] leading-snug">
                        Animazione in alta risoluzione · 10-20 secondi
                      </p>
                    )}
                  </>
                )}
                {active === "before_after" && (
                  <>
                    <p className="text-[12px] font-semibold text-[#1C1C1A] leading-snug">
                      Sto creando il video Prima/Dopo…
                    </p>
                    <p className="text-[10.5px] text-[#6B6A65] leading-snug">
                      Composizione in corso · può richiedere fino a 30 secondi
                    </p>
                  </>
                )}
                {active === "post" && (
                  <p className="text-[12px] font-semibold text-[#1C1C1A] leading-snug">
                    Sto creando il post…
                  </p>
                )}
                {(active === "story" || active === "story_stima") && (
                  <p className="text-[12px] font-semibold text-[#1C1C1A] leading-snug">
                    Sto creando la story…
                  </p>
                )}
              </div>
              )
            ) : active === "reel" || active === "before_after" ? (
              <div className="relative w-full h-full">
                <video
                  src={previewUrl}
                  autoPlay
                  loop
                  muted
                  playsInline
                  onLoadedData={() => setVideoBuffering(false)}
                  onCanPlay={() => setVideoBuffering(false)}
                  onError={() => setVideoBuffering(false)}
                  className="w-full h-full object-contain"
                  data-testid={`social-${active}-preview`}
                />
                {/* iter v26 r17 — Overlay spinner finché il <video> non
                    decodifica il primo frame. Senza questo, l'utente vede
                    un quadrato nero per 1-3s e pensa che sia rotto. */}
                {videoBuffering && (
                  <div
                    className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/40 backdrop-blur-sm"
                    data-testid={`social-${active}-buffering`}
                  >
                    <div className="w-9 h-9 rounded-full border-[3px] border-white border-t-transparent animate-spin" />
                    <p className="text-[11px] text-white/90 leading-snug font-medium">
                      {active === "before_after"
                        ? "Sto preparando il video Prima/Dopo…"
                        : "Sto preparando il reel…"}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              // iter 313 r21 v8 — `object-contain` invece di `object-cover`:
              // su mobile, se il contenitore aspect-[9/16] viene compresso
              // (viewport short), `cover` CROPPA il PNG; `contain` lo scala
              // mantenendo tutto visibile. User feedback: "anteprima tagliata
              // su mobile, solo aprendo da WhatsApp vedo l'immagine intera".
              <img src={previewUrl} alt="" className="w-full h-full object-contain" data-testid={`social-${active}-preview`} />
            )}
          </div>
        </div>
      )}

      {/* iter v27 — Hero Photo Picker per Post e (iter v28 r48) Story.
           iter v28r11: per il Reel non c'è scelta manuale, la cover è
           sempre la prima del ranking AI (le foto migliori).
           iter v28 r48: Story ora ha photo_id come Post → mostra picker. */}
      {(active === "post" || active === "story") &&
        Array.isArray(manifest?.property?.photos) &&
        manifest.property.photos.filter((p) => p.id).length > 1 && (
          <div className="mb-3" data-testid="hero-photo-picker">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#9A968B] mb-1.5">
              Quale foto vuoi usare?
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
              {/* Tile "Auto" — default smart pick */}
              <button
                onClick={() => setHeroPhotoId && setHeroPhotoId(null)}
                className={`relative flex-shrink-0 rounded-md overflow-hidden border-2 transition-all flex flex-col items-center justify-center ${
                  !heroPhotoId
                    ? "border-[#7C8B6E] ring-2 ring-[#7C8B6E]/30 bg-[#F1F4ED]"
                    : "border-[#EAE7DF] hover:border-[#7C8B6E]/50 bg-white"
                }`}
                style={{ width: 72, height: 72 }}
                data-testid="hero-photo-auto"
                title="Scelta automatica della foto migliore"
              >
                <div className="text-[10px] font-bold text-[#7C8B6E] leading-tight">Auto</div>
                <div className="text-[8.5px] text-[#9A968B] mt-0.5 leading-tight px-1 text-center">
                  scelta smart
                </div>
              </button>

              {manifest.property.photos
                .filter((p) => p.id && p.url)
                .filter((p) => {
                  // iter v28 r45 — Escludi planimetrie dal photo picker:
                  // se l'agente sceglie una planimetria come hero del post
                  // viene visualizzata male (sfondo bianco con titolo
                  // sovrapposto). La hero deve essere una foto vera.
                  const kind = (p.photo_kind || p.kind || "").toLowerCase();
                  return kind !== "plan" && kind !== "planimetria" && kind !== "floorplan";
                })
                .map((p, idx) => {
                  const selected = heroPhotoId === p.id;
                  return (
                    <button
                      key={p.id}
                      onClick={() => setHeroPhotoId && setHeroPhotoId(p.id)}
                      className={`relative flex-shrink-0 rounded-md overflow-hidden border-2 transition-all ${
                        selected
                          ? "border-[#7C8B6E] ring-2 ring-[#7C8B6E]/30"
                          : "border-[#EAE7DF] hover:border-[#7C8B6E]/50"
                      }`}
                      style={{ width: 72, height: 72 }}
                      data-testid={`hero-photo-${p.id}`}
                      title={p.room_type || `Foto ${idx + 1}`}
                    >
                      <img
                        src={resolveManifestPhotoUrl(p.url)}
                        alt={p.room_type || `foto-${idx + 1}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      {selected && <div className="absolute inset-0 bg-[#7C8B6E]/15" />}
                      {idx === 0 && !selected && !heroPhotoId && (
                        <div className="absolute bottom-0 inset-x-0 bg-[#7C8B6E]/90 text-white text-[8.5px] uppercase tracking-[0.1em] font-bold text-center py-0.5">
                          Auto
                        </div>
                      )}
                    </button>
                  );
                })}
            </div>
            <div className="text-[10px] text-[#9A968B] mt-1 leading-snug">
              {heroPhotoId
                ? "Foto scelta manualmente. Tocca \"Auto\" per tornare alla scelta automatica."
                : "Pronto sceglie la foto migliore. Puoi sostituirla con un click."}
            </div>
          </div>
        )}

      {/* iter v28 r142 — User M1059: «Reel e Prima/Dopo solo 9:16, elimina
          gli altri formati». AspectToggle rimosso per entrambi: l'aspect
          è hard-coded a 9x16 nel parent state (default useState("9x16")). */}

      {/* iter 313 r21 v21 — Toggle GLOBALE "Mostra prezzo".
           Default OFF: molti annunci sui social funzionano meglio senza prezzo
           (i lead chiedono info via DM invece di scartare in autonomia).
           Visibile per: Post, Story, Reel, Open House, Venduto, Prima/Dopo.
           iter v28 r68 — Se l'annuncio ha già `hide_price=true` (Trattativa
           riservata) il toggle è inutile perché il prezzo non esiste nemmeno
           come dato pubblicabile: lo nascondiamo. */}
      {(active === "post" || active === "story" || active === "reel" ||
        active === "occ_open_house" || active === "occ_venduto" || active === "before_after") &&
       !manifest?.property?.hide_price && (
        <label
          className="mb-3 flex items-center gap-2.5 px-2 py-2 rounded-md hover:bg-[#F1F4ED] cursor-pointer select-none"
          data-testid="social-show-price-toggle"
        >
          <input
            type="checkbox"
            checked={reelShowPrice}
            onChange={(e) => setReelShowPrice(e.target.checked)}
            className="w-4 h-4 accent-[#7C8B6E] cursor-pointer"
          />
          <div className="flex-1 min-w-0">
            <div className="text-[12px] font-medium text-[#1C1C1A]">Mostra il prezzo</div>
            <div className="text-[10.5px] text-[#9A968B] leading-tight mt-0.5">
              Default OFF · senza prezzo i lead ti chiedono info via messaggio invece di scartare da soli
            </div>
          </div>
        </label>
      )}

      {/* iter v28 r474 — User M1385: «togli 16:9 ovunque nell'Editor social».
          Il 16:9 era pensato per FB/LinkedIn ma in pratica nessuno lo usa
          sui social di un agente immobiliare italiano (IG è 1:1 o 4:5,
          stessi feed FB). Rimosso. */}
      {active === "post" && (
        <AspectToggle
          value={postAspect}
          onChange={setPostAspect}
          testIdPrefix="post"
          options={[
            { v: "1x1", lbl: "1:1", hint: "Feed IG/FB" },
            { v: "4x5", lbl: "4:5", hint: "Portrait IG" },
          ]}
        />
      )}

      {/* iter v28 r142 — User M1059: Prima/Dopo solo 9:16. AspectToggle rimosso. */}

      {/* Toggle "prima/dopo" vs "oggi/domani" — solo per before_after.
          Le animazioni cambiano in base al layout scelto, qui scegli SOLO
          le etichette: "PRIMA→DOPO" per arreda spazio vuoto, "OGGI→DOMANI"
          per restyling/refresh di una stanza già arredata. */}
      {active === "before_after" && (
        <>
          {/* iter 264: Spiegazione + restrizioni Prima/Dopo.
              Prima/Dopo è SOLO per stanze trasformate (virtual staging
              o restyle). Non si accoppiano 2 foto qualsiasi. Se mancano
              staged photos, qui c'è il prompt che spiega cosa fare. */}
          {!manifest?.formats?.before_after?.available ? (
            <div
              className="mb-3 p-3 rounded-md bg-amber-50 border border-amber-200 text-[12px] text-amber-900 leading-relaxed"
              data-testid="ba-empty-state"
            >
              <strong className="font-semibold block mb-1">Manca una foto trasformata</strong>
              Il Prima/Dopo funziona solo con stanze che hai <strong>arredato virtualmente</strong> (spazio vuoto → arredato)
              o di cui hai fatto un <strong>restyling</strong> (arredo vecchio → nuovo).
              <div className="mt-2 text-[11px] text-amber-800/85">
                Vai negli <strong>Strumenti foto</strong> → "Arreda stanza vuota" o "Rinnova arredo" e applica l'effetto a una foto, poi torna qui.
              </div>
            </div>
          ) : (
            <>
              {/* Mini-info sulle foto disponibili */}
              <div className="mb-2 text-[11px] text-[#7C8B6E] leading-snug px-1" data-testid="ba-info-staged">
                <strong>{manifest.formats.before_after.staged_count || 0}</strong>{" "}
                {manifest.formats.before_after.staged_count === 1 ? "foto trasformata" : "foto trasformate"} disponibili
                {manifest.formats.before_after.has_staged_furnish && manifest.formats.before_after.has_restyle && " · staging + restyle"}
                {manifest.formats.before_after.has_staged_furnish && !manifest.formats.before_after.has_restyle && " · solo virtual staging"}
                {!manifest.formats.before_after.has_staged_furnish && manifest.formats.before_after.has_restyle && " · solo restyling"}
              </div>

              <div className="mb-3" data-testid="ba-label-kind-toggle">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#9A968B] mb-1.5 text-center">
                  Cosa stai mostrando?
                </div>
                <div className="flex items-stretch justify-center gap-1.5 bg-[#F1F4ED] rounded-md p-1">
                  {[
                    {
                      v: "prima_dopo",
                      lbl: "PRIMA → DOPO",
                      hint: "Spazio vuoto → arredato",
                      disabled: !manifest.formats.before_after.has_staged_furnish,
                    },
                    {
                      v: "oggi_domani",
                      lbl: "OGGI → DOMANI",
                      hint: "Restyling stanza",
                      disabled: !manifest.formats.before_after.has_restyle,
                    },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      onClick={() => !opt.disabled && setBaLabelKind(opt.v)}
                      disabled={opt.disabled}
                      className={`flex-1 px-2 py-2 rounded text-center transition-colors ${
                        opt.disabled
                          ? "opacity-40 cursor-not-allowed"
                          : baLabelKind === opt.v
                          ? "bg-white text-[#1C1C1A] shadow-sm"
                          : "text-[#6B6A65] hover:text-[#1C1C1A]"
                      }`}
                      data-testid={`ba-label-${opt.v}`}
                      title={opt.disabled ? "Nessuna foto compatibile con questa modalità" : ""}
                    >
                      <div className="text-[10.5px] uppercase tracking-[0.14em] font-bold leading-tight">{opt.lbl}</div>
                      <div
                        className={`text-[9.5px] mt-0.5 ${
                          baLabelKind === opt.v && !opt.disabled ? "text-[#7C8B6E]" : "text-[#9A968B]"
                        }`}
                      >
                        {opt.hint}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* iter v26: Selettore foto trasformata.
                  iter v28 r143 — Multi-stanza (max 4): click = toggle nella
                  lista ordinata. Mostra badge "1", "2", "3", "4" sulle foto
                  selezionate. La caption "ECCO COME POTREBBE DIVENTARE"
                  appare solo sulla prima nella sequenza video. */}
              {manifest.formats.before_after.photos && manifest.formats.before_after.photos.length > 0 && (
                <div className="mb-3" data-testid="ba-photo-picker">
                  <div className="flex items-baseline justify-between mb-1.5">
                    <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#9A968B]">
                      Quali stanze vuoi mostrare?
                    </div>
                    <div className="text-[10px] text-[#7C8B6E] font-semibold">
                      {baPhotoIds.length}/4 selezionate
                    </div>
                  </div>
                  <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
                    {manifest.formats.before_after.photos
                      .filter((p) => {
                        if (baLabelKind === "prima_dopo") return p.kind === "staging";
                        if (baLabelKind === "oggi_domani") return p.kind === "restyle";
                        return true;
                      })
                      .map((p) => {
                        const orderIdx = baPhotoIds.indexOf(p.photo_id);
                        const selected = orderIdx >= 0;
                        const order = selected ? orderIdx + 1 : null;
                        const thumbSrc = p.thumb_path ? authedFileUrl(p.thumb_path, false, 200) : null;
                        const atLimit = baPhotoIds.length >= 4 && !selected;
                        return (
                          <button
                            key={p.photo_id}
                            onClick={() => {
                              if (!setBaPhotoIds) {
                                // back-compat: vecchia API single-select
                                setBaPhotoId && setBaPhotoId(p.photo_id);
                                return;
                              }
                              if (selected) {
                                // Toggle off (preserva ordine altri)
                                setBaPhotoIds(baPhotoIds.filter((id) => id !== p.photo_id));
                              } else if (!atLimit) {
                                // Aggiungi in coda (max 4)
                                setBaPhotoIds([...baPhotoIds, p.photo_id]);
                              }
                            }}
                            disabled={atLimit}
                            className={`relative flex-shrink-0 rounded-md overflow-hidden border-2 transition-all ${
                              selected
                                ? "border-[#7C8B6E] ring-2 ring-[#7C8B6E]/30"
                                : atLimit
                                ? "border-[#EAE7DF] opacity-40 cursor-not-allowed"
                                : "border-[#EAE7DF] hover:border-[#7C8B6E]/50"
                            }`}
                            style={{ width: 72, height: 72 }}
                            data-testid={`ba-photo-${p.photo_id}`}
                            title={atLimit ? "Massimo 4 stanze" : (p.room_type || p.style || "Foto trasformata")}
                          >
                            {thumbSrc ? (
                              <img
                                src={thumbSrc}
                                alt={p.room_type || "thumb"}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                            ) : (
                              <div className="w-full h-full bg-[#F1F4ED] flex items-center justify-center text-[10px] text-[#7C8B6E]">
                                {p.kind === "restyle" ? "Restyle" : "Staging"}
                              </div>
                            )}
                            {selected && (
                              <>
                                <div className="absolute inset-0 bg-[#7C8B6E]/15" />
                                <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-[#7C8B6E] text-white text-[11px] font-bold flex items-center justify-center shadow-sm" data-testid={`ba-photo-order-${p.photo_id}`}>
                                  {order}
                                </div>
                              </>
                            )}
                          </button>
                        );
                      })}
                  </div>
                  <div className="text-[10px] text-[#9A968B] mt-1 leading-snug">
                    Tocca per aggiungere fino a <strong>4 stanze</strong>. La scritta «ECCO COME POTREBBE DIVENTARE» appare solo sulla prima.
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </>
  );
}
